
import React, { useState } from 'react';
import { Layout } from '../../components/feature/Layout';

const OverviewPage: React.FC = () => {
  const [timePeriod, setTimePeriod] = useState('today');
  const [supplierSortBy, setSupplierSortBy] = useState('requests');
  const [modelSortBy, setModelSortBy] = useState('requests');
  const [healthTimeGranularity, setHealthTimeGranularity] = useState('hour'); // 'hour' or 'day'

  const timePeriods = [
    { value: 'today', label: '今日' },
    { value: 'yesterday', label: '昨日' },
    { value: 'week', label: '本周' },
    { value: 'month', label: '本月' },
  ];

  // 顶部KPI数据
  const kpiData = {
    userTotal: 12345,
    userNew: timePeriod === 'today' ? 86 : 245,
    userActive: timePeriod === 'today' ? 320 : 1280,
    rechargeTotal: 582000,
    rechargeAmount: timePeriod === 'today' ? 32500 : 125000,
    consumeTotal: 458000,
    consumeAmount: timePeriod === 'today' ? 28600 : 98000,
    callTotal: 3280000,
    callCount: timePeriod === 'today' ? 15420 : 68500,
    tokenTotal: 1230000,
    tokenConsume: timePeriod === 'today' ? 150000 : 680000,
  };

  // 趋势数据（近7天）
  const trendData = {
    labels: ['第1天', '第2天', '第3天', '第4天', '第5天', '第6天', '第7天'],
    recharge: [45000, 52000, 48000, 58000, 62000, 55000, 68000],
    consume: [32000, 38000, 42000, 35000, 48000, 52000, 58000],
    newUsers: [28, 32, 35, 29, 42, 38, 45],
    activeUsers: [180, 220, 195, 245, 280, 265, 320],
  };

  // 系统健康度数据
  const getHealthData = () => {
    if (timePeriod === 'today' && healthTimeGranularity === 'hour') {
      return {
        labels: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'],
        successRate: [97.2, 96.8, 98.1, 97.5, 98.3, 97.8, 98.1],
        avgDelay: [2.8, 3.2, 2.5, 2.9, 2.3, 2.7, 2.6],
        requestFreq: [850, 920, 1200, 1580, 1680, 1420, 1100]
      };
    } else {
      return {
        labels: ['第1天', '第2天', '第3天', '第4天', '第5天', '第6天', '第7天'],
        successRate: [96.8, 97.2, 96.5, 97.8, 96.9, 97.5, 98.1],
        avgDelay: [3.2, 2.8, 3.5, 2.9, 3.1, 2.7, 3.0],
        requestFreq: [1200, 1350, 1180, 1420, 1580, 1465, 1680]
      };
    }
  };

  const healthData = getHealthData();

  // 热门供应商TOP10
  const topSuppliers = [
    { rank: 1, name: 'OpenAI', requests: 15420, tokens: 2100000, ratio: 32 },
    { rank: 2, name: 'DeepSeek', requests: 12350, tokens: 1800000, ratio: 26 },
    { rank: 3, name: 'SiliconFlow', requests: 8960, tokens: 1300000, ratio: 19 },
    { rank: 4, name: 'NovitaAI', requests: 6780, tokens: 900000, ratio: 14 },
    { rank: 5, name: 'Claude', requests: 4230, tokens: 600000, ratio: 9 },
    { rank: 6, name: 'Gemini', requests: 3180, tokens: 500000, ratio: 7 },
    { rank: 7, name: 'Qwen', requests: 2850, tokens: 400000, ratio: 6 },
    { rank: 8, name: 'GLM', requests: 2120, tokens: 300000, ratio: 4 },
    { rank: 9, name: 'Baichuan', requests: 1680, tokens: 200000, ratio: 3 },
    { rank: 10, name: 'ChatGLM', requests: 1250, tokens: 150000, ratio: 2 },
  ];

  // 热门模型TOP10
  const topModels = [
    { rank: 1, name: 'GPT-4o', requests: 9180, tokens: 1500000, ratio: 28 },
    { rank: 2, name: 'DeepSeek-V3', requests: 7650, tokens: 1200000, ratio: 23 },
    { rank: 3, name: 'Claude-3.5-Sonnet', requests: 5420, tokens: 800000, ratio: 17 },
    { rank: 4, name: 'Qwen-2.5-72B', requests: 4320, tokens: 700000, ratio: 13 },
    { rank: 5, name: 'GLM-4-Plus', requests: 3180, tokens: 500000, ratio: 10 },
    { rank: 6, name: 'GPT-3.5-Turbo', requests: 2850, tokens: 400000, ratio: 9 },
    { rank: 7, name: 'Claude-3-Haiku', requests: 2120, tokens: 300000, ratio: 6 },
    { rank: 8, name: 'Gemini-Pro', requests: 1680, tokens: 250000, ratio: 5 },
    { rank: 9, name: 'Baichuan2-13B', requests: 1250, tokens: 200000, ratio: 4 },
    { rank: 10, name: 'ChatGLM3-6B', requests: 980, tokens: 150000, ratio: 3 },
  ];

  // 异常请求分析
  const errorAnalysis = [
    { type: '供应商超时', count: 45, ratio: 35, color: '#ef4444' },
    { type: '模型限流', count: 32, ratio: 25, color: '#f97316' },
    { type: '参数错误', count: 28, ratio: 22, color: '#eab308' },
    { type: '余额不足', count: 15, ratio: 12, color: '#3b82f6' },
    { type: '其他错误', count: 8, ratio: 6, color: '#6b7280' },
  ];

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatToken = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (num: number) => {
    return '¥' + num.toLocaleString();
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* 顶部导航栏 */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <h1 className="text-2xl font-bold text-gray-800">数据概览</h1>
              <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
                {timePeriods.map((period) => (
                  <button
                    key={period.value}
                    onClick={() => setTimePeriod(period.value)}
                    className={`px-5 py-2 rounded-md text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      timePeriod === period.value
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="text-sm text-gray-500">
              最后更新时间：{new Date().toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </div>
          </div>
        </div>

        {/* 顶部KPI卡片区 - 第一行 */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">用户总数</p>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(kpiData.userTotal)}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <i className="ri-user-line text-blue-600 text-2xl"></i>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {timePeriod === 'today' ? '今日新增用户' : '新增用户'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(kpiData.userNew)}</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <i className="ri-user-add-line text-green-600 text-2xl"></i>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {timePeriod === 'today' ? '今日活跃用户数' : '活跃用户数'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(kpiData.userActive)}</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                <i className="ri-user-star-line text-purple-600 text-2xl"></i>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {timePeriod === 'today' ? '今日调用次数' : '调用次数'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(kpiData.callCount)}</p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <i className="ri-phone-line text-orange-600 text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* 顶部KPI卡片区 - 第二行 */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">充值总额</p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(kpiData.rechargeTotal)}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-50 rounded-lg flex items-center justify-center">
                <i className="ri-wallet-line text-indigo-600 text-2xl"></i>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {timePeriod === 'today' ? '今日充值金额' : '充值金额'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(kpiData.rechargeAmount)}</p>
              </div>
              <div className="w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-cyan-600 text-2xl"></i>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">消费总额</p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(kpiData.consumeTotal)}</p>
              </div>
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center">
                <i className="ri-shopping-cart-line text-teal-600 text-2xl"></i>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {timePeriod === 'today' ? '今日消费金额' : '消费金额'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(kpiData.consumeAmount)}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <i className="ri-bank-card-line text-emerald-600 text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* 顶部KPI卡片区 - 第三行 */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">总调用次数</p>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(kpiData.callTotal)}</p>
              </div>
              <div className="w-12 h-12 bg-pink-50 rounded-lg flex items-center justify-center">
                <i className="ri-bar-chart-line text-pink-600 text-2xl"></i>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {timePeriod === 'today' ? '今日Token消耗' : 'Token消耗'}
                </p>
                <p className="text-3xl font-bold text-gray-900">{formatToken(kpiData.tokenConsume)}</p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <i className="ri-cpu-line text-red-600 text-2xl"></i>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">累计Token消耗</p>
                <p className="text-3xl font-bold text-gray-900">{formatToken(kpiData.tokenTotal)}</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <i className="ri-database-2-line text-amber-600 text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* 中部核心图表区 - 第一行：交易趋势图 */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800 flex items-center">
              <i className="ri-line-chart-line text-blue-600 text-xl mr-2"></i>
              交易趋势（近7日）
            </h3>
          </div>
          <div className="h-80 relative">
            <svg className="w-full h-full" viewBox="0 0 700 300">
              {/* 网格线 */}
              {[0, 1, 2, 3, 4].map((i) => (
                <line
                  key={i}
                  x1="50"
                  y1={50 + i * 50}
                  x2="680"
                  y2={50 + i * 50}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
              ))}
              
              {/* 充值折线 */}
              <polyline
                points={trendData.recharge.map((value, index) => {
                  const x = 50 + (index * 90) + 45;
                  const maxValue = Math.max(...trendData.recharge, ...trendData.consume);
                  const y = 250 - ((value / maxValue) * 200);
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              
              {/* 充值数据点 */}
              {trendData.recharge.map((value, index) => {
                const x = 50 + (index * 90) + 45;
                const maxValue = Math.max(...trendData.recharge, ...trendData.consume);
                const y = 250 - ((value / maxValue) * 200);
                return (
                  <circle
                    key={`recharge-${index}`}
                    cx={x}
                    cy={y}
                    r="5"
                    fill="#3b82f6"
                    stroke="white"
                    strokeWidth="2"
                  />
                );
              })}
              
              {/* 消费折线 */}
              <polyline
                points={trendData.consume.map((value, index) => {
                  const x = 50 + (index * 90) + 45;
                  const maxValue = Math.max(...trendData.recharge, ...trendData.consume);
                  const y = 250 - ((value / maxValue) * 200);
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke="#10b981"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              
              {/* 消费数据点 */}
              {trendData.consume.map((value, index) => {
                const x = 50 + (index * 90) + 45;
                const maxValue = Math.max(...trendData.recharge, ...trendData.consume);
                const y = 250 - ((value / maxValue) * 200);
                return (
                  <circle
                    key={`consume-${index}`}
                    cx={x}
                    cy={y}
                    r="5"
                    fill="#10b981"
                    stroke="white"
                    strokeWidth="2"
                  />
                );
              })}
              
              {/* X轴标签 */}
              {trendData.labels.map((label, index) => (
                <text
                  key={label}
                  x={50 + (index * 90) + 45}
                  y="280"
                  textAnchor="middle"
                  fontSize="12"
                  fill="#6b7280"
                >
                  {label}
                </text>
              ))}
            </svg>
          </div>
          <div className="flex items-center justify-center space-x-8 mt-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
              <span className="text-sm text-gray-600">充值</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-600 rounded-full"></div>
              <span className="text-sm text-gray-600">消费</span>
            </div>
          </div>
        </div>

        {/* 中部核心图表区 - 第二行：用户增长图 */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800 flex items-center">
              <i className="ri-user-line text-purple-600 text-xl mr-2"></i>
              用户增长（近7日）
            </h3>
          </div>
          <div className="h-80 relative">
            <svg className="w-full h-full" viewBox="0 0 700 300">
              {/* 网格线 */}
              {[0, 1, 2, 3, 4].map((i) => (
                <line
                  key={i}
                  x1="50"
                  y1={50 + i * 50}
                  x2="680"
                  y2={50 + i * 50}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
              ))}
              
              {/* 新用户折线 */}
              <polyline
                points={trendData.newUsers.map((value, index) => {
                  const x = 50 + (index * 90) + 45;
                  const maxValue = Math.max(...trendData.newUsers);
                  const y = 250 - ((value / maxValue) * 200);
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke="#a855f7"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              
              {/* 新用户数据点 */}
              {trendData.newUsers.map((value, index) => {
                const x = 50 + (index * 90) + 45;
                const maxValue = Math.max(...trendData.newUsers);
                const y = 250 - ((value / maxValue) * 200);
                return (
                  <circle
                    key={`new-${index}`}
                    cx={x}
                    cy={y}
                    r="5"
                    fill="#a855f7"
                    stroke="white"
                    strokeWidth="2"
                  />
                );
              })}
              
              {/* 活跃用户折线 */}
              <polyline
                points={trendData.activeUsers.map((value, index) => {
                  const x = 50 + (index * 90) + 45;
                  const maxValue = Math.max(...trendData.activeUsers);
                  const y = 250 - ((value / maxValue) * 200);
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke="#f97316"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              
              {/* 活跃用户数据点 */}
              {trendData.activeUsers.map((value, index) => {
                const x = 50 + (index * 90) + 45;
                const maxValue = Math.max(...trendData.activeUsers);
                const y = 250 - ((value / maxValue) * 200);
                return (
                  <circle
                    key={`active-${index}`}
                    cx={x}
                    cy={y}
                    r="5"
                    fill="#f97316"
                    stroke="white"
                    strokeWidth="2"
                  />
                );
              })}
              
              {/* X轴标签 */}
              {trendData.labels.map((label, index) => (
                <text
                  key={label}
                  x={50 + (index * 90) + 45}
                  y="280"
                  textAnchor="middle"
                  fontSize="12"
                  fill="#6b7280"
                >
                  {label}
                </text>
              ))}
            </svg>
          </div>
          <div className="flex items-center justify-center space-x-8 mt-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
              <span className="text-sm text-gray-600">新用户</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-orange-600 rounded-full"></div>
              <span className="text-sm text-gray-600">活跃用户</span>
            </div>
          </div>
        </div>

        {/* 中部核心图表区 - 第三行：系统健康度 + 异常请求分析 */}
        <div className="grid grid-cols-2 gap-6">
          {/* 系统健康度 */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800 flex items-center">
                <i className="ri-heart-pulse-line text-green-600 text-xl mr-2"></i>
                系统健康度
              </h3>
              <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setHealthTimeGranularity('hour')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    healthTimeGranularity === 'hour'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  按小时
                </button>
                <button
                  onClick={() => setHealthTimeGranularity('day')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    healthTimeGranularity === 'day'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  按日
                </button>
              </div>
            </div>
            
            <div className="h-96 relative">
              <svg className="w-full h-full" viewBox="0 0 500 380">
                {/* 网格线 */}
                {[0, 1, 2, 3, 4].map((i) => (
                  <line
                    key={i}
                    x1="50"
                    y1={40 + i * 40}
                    x2="480"
                    y2={40 + i * 40}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                  />
                ))}
                
                {/* 左Y轴标签 - 成功率 */}
                {[100, 99, 98, 97, 96].map((value, index) => (
                  <text
                    key={`left-${value}`}
                    x="35"
                    y={45 + index * 40}
                    textAnchor="end"
                    fontSize="10"
                    fill="#6b7280"
                  >
                    {value}%
                  </text>
                ))}
                
                {/* 右Y轴标签 - 延迟 */}
                {[4, 3, 2, 1, 0].map((value, index) => (
                  <text
                    key={`right-${value}`}
                    x="495"
                    y={45 + index * 40}
                    textAnchor="start"
                    fontSize="10"
                    fill="#6b7280"
                  >
                    {value}s
                  </text>
                ))}
                
                {/* 成功率折线 */}
                <polyline
                  points={healthData.successRate.map((value, index) => {
                    const x = 50 + (index * 60) + 30;
                    const y = 240 - ((value - 95) / 5 * 200);
                    return `${x},${y}`;
                  }).join(' ')}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                
                {/* 成功率数据点 */}
                {healthData.successRate.map((value, index) => {
                  const x = 50 + (index * 60) + 30;
                  const y = 240 - ((value - 95) / 5 * 200);
                  return (
                    <circle
                      key={`success-${index}`}
                      cx={x}
                      cy={y}
                      r="3"
                      fill="#10b981"
                      stroke="white"
                      strokeWidth="1"
                    />
                  );
                })}
                
                {/* 延迟折线 */}
                <polyline
                  points={healthData.avgDelay.map((value, index) => {
                    const x = 50 + (index * 60) + 30;
                    const y = 240 - ((value / 4) * 200);
                    return `${x},${y}`;
                  }).join(' ')}
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                
                {/* 延迟数据点 */}
                {healthData.avgDelay.map((value, index) => {
                  const x = 50 + (index * 60) + 30;
                  const y = 240 - ((value / 4) * 200);
                  return (
                    <circle
                      key={`delay-${index}`}
                      cx={x}
                      cy={y}
                      r="3"
                      fill="#3b82f6"
                      stroke="white"
                      strokeWidth="1"
                    />
                  );
                })}
                
                {/* 请求频率柱状图 */}
                {healthData.requestFreq.map((value, index) => {
                  const x = 50 + (index * 60) + 20;
                  const maxFreq = Math.max(...healthData.requestFreq);
                  const height = (value / maxFreq) * 80;
                  return (
                    <rect
                      key={`freq-${index}`}
                      x={x}
                      y={320 - height}
                      width="20"
                      height={height}
                      fill="#a855f7"
                      opacity="0.7"
                      rx="2"
                    />
                  );
                })}
                
                {/* X轴标签 */}
                {healthData.labels.map((label, index) => (
                  <text
                    key={label}
                    x={50 + (index * 60) + 30}
                    y="360"
                    textAnchor="middle"
                    fontSize="10"
                    fill="#6b7280"
                  >
                    {label}
                  </text>
                ))}
              </svg>
            </div>
            
            <div className="flex items-center justify-center space-x-6 mt-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                <span className="text-xs text-gray-600">请求成功率</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                <span className="text-xs text-gray-600">平均延迟</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                <span className="text-xs text-gray-600">请求频率</span>
              </div>
            </div>
          </div>

          {/* 异常请求分析 */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800 flex items-center">
                <i className="ri-error-warning-line text-red-600 text-xl mr-2"></i>
                异常请求分析
              </h3>
            </div>
            <div className="flex flex-col items-center">
              <div className="relative w-48 h-48 mb-6">
                <svg className="w-48 h-48 transform -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18"
                    cy="18"
                    r="15.9155"
                    fill="none"
                    stroke="#f3f4f6"
                    strokeWidth="3"
                  />
                  {errorAnalysis.map((error, index) => {
                    const offset = errorAnalysis.slice(0, index).reduce((sum, e) => sum + e.ratio, 0);
                    return (
                      <circle
                        key={error.type}
                        cx="18"
                        cy="18"
                        r="15.9155"
                        fill="none"
                        stroke={error.color}
                        strokeWidth="3"
                        strokeDasharray={`${error.ratio}, 100`}
                        strokeDashoffset={-offset}
                      />
                    );
                  })}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">128</div>
                    <div className="text-sm text-gray-500">异常总数</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3 w-full">
                {errorAnalysis.map((error) => (
                  <div key={error.type} className="flex items-center justify-between text-sm p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: error.color }}
                      ></div>
                      <span className="text-gray-700">{error.type}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-gray-900">{error.count}次</span>
                      <span className="text-gray-500 ml-2">({error.ratio}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 底部排行榜 */}
        <div className="grid grid-cols-2 gap-6">
          {/* 热门供应商TOP10 */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800 flex items-center">
                <i className="ri-building-line text-blue-600 text-xl mr-2"></i>
                热门供应商TOP10
              </h3>
              <select
                value={supplierSortBy}
                onChange={(e) => setSupplierSortBy(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 cursor-pointer pr-8 bg-white hover:border-gray-400 transition-colors duration-200"
              >
                <option value="requests">按请求数</option>
                <option value="tokens">按Token消耗</option>
              </select>
            </div>
            <div className="space-y-2">
              {topSuppliers.map((supplier) => (
                <div 
                  key={supplier.rank} 
                  className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${
                    supplier.rank === 1 ? 'bg-blue-50 border border-blue-200' : 
                    supplier.rank === 2 ? 'bg-gray-50 border border-gray-200' :
                    supplier.rank === 3 ? 'bg-orange-50 border border-orange-200' :
                    'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      supplier.rank === 1 ? 'bg-blue-600 text-white' : 
                      supplier.rank === 2 ? 'bg-gray-400 text-white' :
                      supplier.rank === 3 ? 'bg-orange-400 text-white' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {supplier.rank}
                    </span>
                    <span className="font-medium text-gray-900">{supplier.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">
                      {supplierSortBy === 'requests' ? formatNumber(supplier.requests) : formatToken(supplier.tokens)}
                    </p>
                    <p className="text-xs text-gray-500">占比{supplier.ratio}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 热门模型TOP10 */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800 flex items-center">
                <i className="ri-cpu-line text-green-600 text-xl mr-2"></i>
                热门模型TOP10
              </h3>
              <select
                value={modelSortBy}
                onChange={(e) => setModelSortBy(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 cursor-pointer pr-8 bg-white hover:border-gray-400 transition-colors duration-200"
              >
                <option value="requests">按请求数</option>
                <option value="tokens">按Token消耗</option>
              </select>
            </div>
            <div className="space-y-2">
              {topModels.map((model) => (
                <div 
                  key={model.rank} 
                  className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${
                    model.rank === 1 ? 'bg-green-50 border border-green-200' : 
                    model.rank === 2 ? 'bg-gray-50 border border-gray-200' :
                    model.rank === 3 ? 'bg-orange-50 border border-orange-200' :
                    'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      model.rank === 1 ? 'bg-green-600 text-white' : 
                      model.rank === 2 ? 'bg-gray-400 text-white' :
                      model.rank === 3 ? 'bg-orange-400 text-white' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {model.rank}
                    </span>
                    <span className="font-medium text-gray-900">{model.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">
                      {modelSortBy === 'requests' ? formatNumber(model.requests) : formatToken(model.tokens)}
                    </p>
                    <p className="text-xs text-gray-500">占比{model.ratio}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OverviewPage;
